/**
 * Floor Plan Editor — drag-drop masa düzenleyici + plan/masa CRUD.
 *
 * - "Yeni Plan" butonu: salon planı oluşturur (modal)
 * - "Plan Düzenle" / "Plan Sil": seçili plan üzerinde
 * - Canvas'a TIKLA → o noktada yeni masa yarat (modal'da ad+kapasite+şekil)
 * - Mevcut masaları sürükle → pos_x/pos_y backend'e patch'lenir
 * - Masa üstüne çift tıkla → düzenle/sil
 */
import {
  ActionIcon, Badge, Button, Card, Center, Group, Loader, Menu, Select,
  Stack, Text,
} from "@mantine/core";
import {
  IconDeviceFloppy, IconDots, IconEdit, IconPlus, IconRefresh, IconTrash,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { useResource, useRemove, useUpdate } from "../../../shared/api";
import { PageHeader, EmptyState, ResourceForm, confirmDelete, type FormField } from "../../../shared/ui";

const SHAPE_STYLE: Record<string, { width: number; height: number; borderRadius: string }> = {
  SQUARE: { width: 80, height: 80, borderRadius: "8px" },
  RECTANGLE: { width: 120, height: 80, borderRadius: "8px" },
  ROUND: { width: 80, height: 80, borderRadius: "50%" },
  OVAL: { width: 120, height: 80, borderRadius: "50%" },
};

const STATE_COLOR: Record<string, string> = {
  FREE: "#22c55e",
  OCCUPIED: "#ef4444",
  RESERVED: "#eab308",
  CLEANING: "#94a3b8",
  OUT_OF_SERVICE: "#475569",
};

const SHAPE_OPTIONS = [
  { value: "SQUARE", label: "Kare" },
  { value: "RECTANGLE", label: "Dikdörtgen" },
  { value: "ROUND", label: "Yuvarlak" },
  { value: "OVAL", label: "Oval" },
];

const PLAN_FIELDS: FormField[] = [
  { name: "name", label: "Plan Adı", required: true, placeholder: "Ana Salon, Bahçe, …" },
  { name: "description", label: "Açıklama", type: "textarea" },
  { name: "width", label: "Genişlik (px)", type: "number", min: 200, defaultValue: 1000 },
  { name: "height", label: "Yükseklik (px)", type: "number", min: 200, defaultValue: 600 },
  { name: "sort_order", label: "Sıra", type: "number", defaultValue: 0 },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
];

export function FloorPlanEditorPage() {
  const { data: plansData, isLoading } = useResource<any>("floor-plans");
  const plans: any[] = plansData?.results || plansData || [];
  const [planId, setPlanId] = useState<string | null>(null);

  const { data: tablesData } = useResource<any>("tables", {
    filters: planId ? { floor_plan: planId } : {},
    enabled: !!planId,
  });
  const allTables: any[] = tablesData?.results || tablesData || [];

  const updateTable = useUpdate("tables");
  const removeTable = useRemove("tables");
  const removePlan = useRemove("floor-plans");

  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [dirty, setDirty] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  // Plan create/edit modal
  const [planModal, planModalCtl] = useDisclosure(false);
  const [planEditing, setPlanEditing] = useState<any | null>(null);

  // Table create/edit modal
  const [tableModal, tableModalCtl] = useDisclosure(false);
  const [tableEditing, setTableEditing] = useState<any | null>(null);
  const [pendingPos, setPendingPos] = useState<{ x: number; y: number } | null>(null);

  // Seçili plan değişince pozisyonlar reset
  useEffect(() => {
    const next: Record<string, { x: number; y: number }> = {};
    for (const t of allTables) {
      next[t.id] = { x: t.pos_x || 0, y: t.pos_y || 0 };
    }
    setPositions(next);
    setDirty(false);
  }, [allTables.length, planId]);

  // İlk plan otomatik seçilsin
  useEffect(() => {
    if (!planId && plans.length > 0) setPlanId(plans[0].id);
  }, [plans.length, planId]);

  if (isLoading) return <Center h={300}><Loader /></Center>;

  const currentPlan = plans.find((p) => p.id === planId);

  function startDrag(tableId: string, e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = false;
    const startX = e.clientX;
    const startY = e.clientY;
    const startPos = positions[tableId] || { x: 0, y: 0 };
    function move(ev: PointerEvent) {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) draggingRef.current = true;
      const newX = Math.round(Math.max(0, startPos.x + dx));
      const newY = Math.round(Math.max(0, startPos.y + dy));
      setPositions((p) => ({ ...p, [tableId]: { x: newX, y: newY } }));
      setDirty(true);
    }
    function up() {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    }
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function handleCanvasClick(e: React.MouseEvent) {
    if (!containerRef.current || !planId) return;
    if (draggingRef.current) {
      draggingRef.current = false;
      return;
    }
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.round(e.clientX - rect.left);
    const y = Math.round(e.clientY - rect.top);
    setPendingPos({ x, y });
    setTableEditing(null);
    tableModalCtl.open();
  }

  async function saveAll() {
    if (!dirty) return;
    const failures: { name: string; detail: string }[] = [];
    for (const [id, pos] of Object.entries(positions)) {
      const original = allTables.find((t) => t.id === id);
      if (!original || (original.pos_x === pos.x && original.pos_y === pos.y)) continue;
      try {
        await updateTable.mutateAsync({ id, pos_x: pos.x, pos_y: pos.y });
      } catch (e: any) {
        const data = e?.response?.data;
        const detail =
          (data && (data.detail || JSON.stringify(data))) ||
          e?.message ||
          "Bilinmeyen hata";
        failures.push({ name: original.name, detail });
        // eslint-disable-next-line no-console
        console.error("Masa kaydetme hatası:", original.name, e?.response?.status, data);
      }
    }
    if (failures.length === 0) {
      notifications.show({ message: "Plan kaydedildi", color: "green" });
      setDirty(false);
    } else {
      notifications.show({
        title: `${failures.length} masa kaydedilemedi`,
        message: failures
          .map((f) => `${f.name}: ${f.detail}`)
          .join(" · "),
        color: "red",
        autoClose: 8000,
      });
    }
  }

  async function handleDeleteTable(t: any) {
    if (!(await confirmDelete(`"${t.name}" masası silinsin mi?`))) return;
    try {
      await removeTable.mutateAsync(t.id);
      notifications.show({ message: "Masa silindi", color: "green" });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Silinemedi",
        color: "red",
      });
    }
  }

  async function handleDeletePlan() {
    if (!currentPlan) return;
    if (!(await confirmDelete(
      `"${currentPlan.name}" planı silinsin mi? Plana bağlı masalar plansız kalır.`,
    ))) return;
    try {
      await removePlan.mutateAsync(currentPlan.id);
      notifications.show({ message: "Plan silindi", color: "green" });
      setPlanId(null);
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || "Silinemedi",
        color: "red",
      });
    }
  }

  const tableFields: FormField[] = [
    { name: "name", label: "Ad", required: true, placeholder: "M1, Bahçe-3, …" },
    { name: "capacity", label: "Kapasite (kişi)", type: "number", min: 1, defaultValue: 4 },
    { name: "shape", label: "Şekil", type: "select", options: SHAPE_OPTIONS, defaultValue: "SQUARE" },
  ];

  // Yeni masa oluştururken canvas tıklama konumunu + planı enjekte et
  function transformTablePayload(values: Record<string, any>) {
    if (tableEditing) return values;
    return {
      ...values,
      floor_plan: planId,
      pos_x: pendingPos?.x ?? 0,
      pos_y: pendingPos?.y ?? 0,
    };
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Salon Plan Editörü"
        subtitle="Masaları sürükleyerek konumlandırın · Canvas'a tıklayarak yeni masa ekleyin"
        actions={
          <Group>
            <Button
              variant="light"
              leftSection={<IconPlus size={16} />}
              onClick={() => { setPlanEditing(null); planModalCtl.open(); }}
            >
              Yeni Plan
            </Button>
            <Button
              variant="default"
              leftSection={<IconRefresh size={16} />}
              onClick={() => {
                const next: Record<string, { x: number; y: number }> = {};
                for (const t of allTables) next[t.id] = { x: t.pos_x || 0, y: t.pos_y || 0 };
                setPositions(next);
                setDirty(false);
              }}
              disabled={!dirty}
            >
              Sıfırla
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={16} />}
              onClick={saveAll}
              disabled={!dirty}
              loading={updateTable.isPending}
            >
              Kaydet
            </Button>
          </Group>
        }
      />

      <Group align="flex-end">
        <Select
          style={{ flex: 1 }}
          label="Salon Planı"
          placeholder={plans.length === 0 ? "Henüz plan yok — Yeni Plan ile başlayın" : "Bir plan seçin"}
          data={plans.map((p) => ({ value: p.id, label: p.name }))}
          value={planId}
          onChange={setPlanId}
          disabled={plans.length === 0}
        />
        {currentPlan && (
          <Menu shadow="md" position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="default" size="lg" aria-label="Plan aksiyonları">
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconEdit size={14} />}
                onClick={() => { setPlanEditing(currentPlan); planModalCtl.open(); }}
              >
                Planı Düzenle
              </Menu.Item>
              <Menu.Item
                leftSection={<IconTrash size={14} />}
                color="red"
                onClick={handleDeletePlan}
              >
                Planı Sil
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>

      {!planId ? (
        <EmptyState
          title={plans.length === 0 ? "Henüz plan yok" : "Plan seçilmedi"}
          description={plans.length === 0
            ? "Başlamak için 'Yeni Plan' ile bir salon planı oluşturun."
            : "Düzenlemek için bir salon planı seçin."}
        />
      ) : (
        <Card padding="sm" radius="md" withBorder>
          <Group justify="space-between" mb="sm">
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                {currentPlan?.name} — {currentPlan?.width || 1000}×{currentPlan?.height || 600}px ·
                {" "}{allTables.length} masa
              </Text>
              {dirty && <Badge color="orange">Kaydedilmemiş değişiklik</Badge>}
            </Group>
            <Text size="xs" c="dimmed">Boş alana tıkla → yeni masa · Masaya sürükle → konumlandır</Text>
          </Group>
          {/* Tuval sabit piksel boyutludur (masa koordinatları gerçek x/y).
              Dar ekrana sığmaz → bu sarmalayıcı yatay+dikey kaydırma sağlar
              ki sağdaki/alttaki masalara erişilebilsin. */}
          <div style={{ overflow: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
          <div
            ref={containerRef}
            onClick={handleCanvasClick}
            style={{
              position: "relative",
              width: currentPlan?.width || 1000,
              height: currentPlan?.height || 600,
              background: "repeating-linear-gradient(0deg, transparent 0, transparent 49px, #f0f0f0 49px, #f0f0f0 50px), repeating-linear-gradient(90deg, transparent 0, transparent 49px, #f0f0f0 49px, #f0f0f0 50px)",
              border: "2px solid var(--mantine-color-gray-3)",
              borderRadius: 8,
              overflow: "hidden",
              userSelect: "none",
              cursor: "crosshair",
            }}
          >
            {allTables.map((t) => {
              const pos = positions[t.id] || { x: 0, y: 0 };
              const shape = SHAPE_STYLE[t.shape || "SQUARE"];
              const color = STATE_COLOR[t.state || "FREE"];
              return (
                <div
                  key={t.id}
                  onPointerDown={(e) => startDrag(t.id, e)}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    setTableEditing(t);
                    setPendingPos(null);
                    tableModalCtl.open();
                  }}
                  style={{
                    position: "absolute",
                    left: pos.x,
                    top: pos.y,
                    width: shape.width,
                    height: shape.height,
                    borderRadius: shape.borderRadius,
                    background: color,
                    color: "white",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    cursor: "move",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                    transform: t.rotation ? `rotate(${t.rotation}deg)` : undefined,
                  }}
                  title="Sürükle: konumlandır · Çift tıkla: düzenle"
                >
                  <span style={{ fontSize: 18 }}>{t.name}</span>
                  <span style={{ fontSize: 11, opacity: 0.9 }}>{t.capacity} kişi</span>
                  <ActionIcon
                    size="xs"
                    variant="filled"
                    color="red"
                    onClick={(e) => { e.stopPropagation(); handleDeleteTable(t); }}
                    onPointerDown={(e) => e.stopPropagation()}
                    style={{ position: "absolute", top: 2, right: 2, opacity: 0.85 }}
                    aria-label="Masayı sil"
                  >
                    <IconTrash size={10} />
                  </ActionIcon>
                </div>
              );
            })}
          </div>
          </div>
        </Card>
      )}

      <ResourceForm
        opened={planModal}
        onClose={planModalCtl.close}
        resource="floor-plans"
        title={planEditing ? "Planı Düzenle" : "Yeni Salon Planı"}
        fields={PLAN_FIELDS}
        editId={planEditing?.id || null}
        initialValues={planEditing || undefined}
      />

      <ResourceForm
        opened={tableModal}
        onClose={() => { tableModalCtl.close(); setPendingPos(null); }}
        resource="tables"
        title={tableEditing ? "Masa Düzenle" : "Yeni Masa"}
        fields={tableFields}
        editId={tableEditing?.id || null}
        initialValues={tableEditing || undefined}
        transform={transformTablePayload}
      />
    </Stack>
  );
}
